import { useState } from 'react';
import { API_URL } from '../../config';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { addToCart } from '../../redux/cartSlice';
import { ShoppingBag, Eye, Check, Loader2, X, Star } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../i18n/LanguageContext';

const ProductBox = ({ product, msg, msgColor, index = 0 }) => {
  const dispatch = useDispatch();
  const [status, setStatus] = useState('idle');
  const [showVariantPicker, setShowVariantPicker] = useState(false);
  const { t, dir, getLabel } = useLanguage();

  if (!product) return null;

  const { name, images = [], variants = [], slug, isPack, status: stockStatus, isMostSold, isNewProduct, featured } = product;
  const isOutOfStock = stockStatus === 'out_of_stock';
  const isLowStock = stockStatus === 'low_stock';

  const displayName = getLabel(name) || t('product.unavailable');
  const firstVariant = variants[0] || {};
  const isOnSale = firstVariant.isOnSale && firstVariant.discountPrice > 0;
  const displayPrice = isOnSale ? Number(firstVariant.discountPrice).toFixed(3) : (firstVariant.price || 0).toFixed(3);
  const originalPrice = (firstVariant.price || 0).toFixed(3);
  const discountPct = isOnSale ? Math.round((1 - firstVariant.discountPrice / firstVariant.price) * 100) : 0;

  const handleAddToCart = (variant) => {
    setShowVariantPicker(false);
    setStatus('loading');
    setTimeout(() => {
      const effectiveVariant = {
        ...variant,
        price: (variant.isOnSale && variant.discountPrice > 0) ? variant.discountPrice : variant.price
      };
      const cartItem = { ...product, selectedVariant: effectiveVariant, quantity: 1 };
      dispatch(addToCart(cartItem));
      setStatus('success');
      setTimeout(() => setStatus('idle'), 2000);
    }, 400);
  };

  const onMainButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!variants || variants.length === 0) {
      alert(t('product.notAvailable'));
      return;
    }
    if (variants.length === 1) {
      handleAddToCart(variants[0]);
    } else {
      setShowVariantPicker(true);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-20px' }}
      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1], delay: (index % 12) * 0.06 }}
      whileHover={{ y: -4 }}
      className="group relative bg-white rounded-2xl border border-gray-100 flex flex-col h-full hover:border-emerald-200 transition-colors duration-300 overflow-hidden"
    >
      {/* Badges */}
      <div className="absolute top-2.5 start-2.5 flex flex-col gap-1 z-10">
        {isOnSale && (
          <span className="bg-red-500 text-white px-2 py-0.5 rounded-lg text-[9px] font-bold">-{discountPct}%</span>
        )}
        {isNewProduct && (
          <span className="bg-emerald-500 text-white px-2 py-0.5 rounded-lg text-[9px] font-bold">{t('home.newMsg')}</span>
        )}
      </div>

      {/* Image */}
      <Link to={`/products/${slug}`} className="relative aspect-square overflow-hidden bg-white block">
        <img
          src={images?.[0] ? `${API_URL}/${images[0]}` : "/placeholder.jpg"}
          alt={displayName}
          className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-emerald-600 text-white p-2.5 rounded-full opacity-0 group-hover:opacity-100 scale-75 group-hover:scale-100 transition-all duration-300">
            <Eye className="w-4 h-4" />
          </div>
        </div>
      </Link>

      {/* Content */}
      <div className="p-3.5 flex flex-col flex-grow text-center relative" dir={dir}>

        <h3 className="text-sm font-bold text-gray-800 line-clamp-2 leading-tight mb-1.5 min-h-[2.5rem]">
          {displayName}
        </h3>

        {/* Stars */}
        <div className="flex items-center justify-center gap-0.5 mb-2">
          {[1,2,3,4,5].map(s => {
            const avg = product.avgRating ?? product.averageRating ?? product.rating ?? 0;
            return (
              <Star key={s} className={`w-3 h-3 ${s <= Math.round(avg) ? 'fill-amber-400 text-amber-400' : 'fill-gray-200 text-gray-200'}`} />
            );
          })}
          {(product.avgRating ?? product.averageRating ?? product.rating) > 0 && (
            <span className="text-[9px] font-bold text-gray-400 ms-1">
              ({(product.avgRating ?? product.averageRating ?? product.rating).toFixed(1)})
            </span>
          )}
        </div>

        {/* Variant tags */}
        <div className="flex flex-wrap justify-center gap-1 mb-3 min-h-[1.5rem]">
          {variants && variants.length > 0 ? (
            variants.slice(0, 3).map((v, i) => (
              <span key={i} className={`text-[9px] px-1.5 py-0.5 rounded-md font-bold border ${
                i === 0
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-gray-50 text-gray-500 border-gray-100'
              }`}>
                {v.grammage}
              </span>
            ))
          ) : (
            <span className="text-[9px] text-gray-300">{t('product.unavailable')}</span>
          )}
        </div>

        {/* Price */}
        <div className="flex items-baseline justify-center gap-1 mb-3.5 flex-wrap">
          <span className={`text-xl font-black ${isOnSale ? 'text-red-600' : 'text-gray-900'}`}>{displayPrice}</span>
          <span className="text-xs font-bold text-gray-400">د.ت</span>
          {isOnSale && (
            <span className="text-xs font-bold text-gray-400 line-through">{originalPrice}</span>
          )}
        </div>

        {/* Variant picker */}
        <AnimatePresence>
          {showVariantPicker && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 6 }}
              transition={{ duration: 0.18 }}
              className="absolute bottom-[62px] start-2 end-2 bg-white border-2 border-emerald-600 rounded-2xl p-3 z-20"
            >
              <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-100">
                <span className="text-[11px] font-black text-emerald-700">{t('product.chooseSize')}</span>
                <button onClick={() => setShowVariantPicker(false)} className="text-gray-400 hover:text-red-500 transition-colors">
                  <X size={14} />
                </button>
              </div>
              <div className="flex flex-col gap-1.5 max-h-40 overflow-y-auto">
                {variants.map((v, i) => (
                  <motion.button
                    key={i}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => handleAddToCart(v)}
                    className="w-full flex justify-between items-center py-2 px-3 text-[11px] font-bold border border-emerald-100 bg-emerald-50 text-emerald-800 rounded-xl hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-all"
                  >
                    <span>{v.grammage}</span>
                    <span className="font-black flex items-center gap-1">
                      {v.isOnSale && v.discountPrice > 0 ? (
                        <><span className="text-red-600">{Number(v.discountPrice).toFixed(3)}</span><span className="line-through opacity-50 text-[9px]">{v.price.toFixed(3)}</span></>
                      ) : (v.price || 0).toFixed(3)} د.ت
                    </span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Add to cart button */}
        <motion.button
          onClick={onMainButtonClick}
          disabled={status !== 'idle' || isOutOfStock}
          whileTap={status === 'idle' && !isOutOfStock ? { scale: 0.96 } : {}}
          className={`mt-auto w-full py-2.5 rounded-xl flex items-center justify-center gap-2 transition-colors font-bold text-xs
            ${isOutOfStock ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}
            ${!isOutOfStock && status === 'idle' ? 'bg-emerald-600 text-white hover:bg-emerald-700' : ''}
            ${status === 'loading' ? 'bg-gray-100 text-gray-400 cursor-wait' : ''}
            ${status === 'success' ? 'bg-emerald-500 text-white' : ''}
          `}
        >
          <AnimatePresence mode="wait" initial={false}>
            {status === 'idle' && (
              <motion.span key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} className="flex items-center gap-2">
                <ShoppingBag className="w-3.5 h-3.5" />{t('product.addToCart')}
              </motion.span>
            )}
            {status === 'loading' && (
              <motion.span key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                <Loader2 className="animate-spin w-4 h-4" />
              </motion.span>
            )}
            {status === 'success' && (
              <motion.span key="success" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="flex items-center gap-2">
                <Check className="w-3.5 h-3.5" />{t('product.added')}
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
      </div>
    </motion.div>
  );
};

export default ProductBox;
